import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";

interface TooltipData {
  risk: string;
  icon: LucideIcon;
  detail: string;
  cityStats: string;
  avgDamage: string;
  avgDowntime: string;
  affectedPractices: string;
  funFact: string;
}

interface AnimatedTooltipProps {
  data: TooltipData;
}

const AnimatedTooltip = ({ data }: AnimatedTooltipProps) => {
  const Icon = data.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="fixed inset-0 z-50 flex items-center justify-center 
                 p-3 backdrop-blur-sm bg-black/40 pointer-events-auto
                 lg:absolute lg:inset-auto lg:top-1/2 lg:left-1/2 
                 lg:-translate-x-1/2 lg:-translate-y-1/2 
                 lg:backdrop-blur-none lg:bg-transparent lg:p-0"
      style={{ fontFamily: 'Manrope' }}
    >
      <div className="bg-card border-2 border-accent/80 
                      rounded-2xl p-3.5 shadow-2xl 
                      max-w-[280px] w-full
                      animate-in fade-in-0 zoom-in-95"
      >
      {/* Header: Icon + Name + Percentage */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon className="w-4 h-4 text-accent flex-shrink-0" />
          <h3 className="font-bold text-sm text-foreground">
            {data.risk}
          </h3>
        </div>
        <span className="text-lg font-bold text-accent tabular-nums">
          95%
        </span>
      </div>
      
      {/* Hero Stat: Damage Amount */}
      <div className="text-center py-2.5 mb-2 bg-accent/5 rounded-lg border border-accent/10">
        <p className="text-2xl font-bold text-accent tabular-nums mb-0.5">
          {data.avgDamage}
        </p>
        <p className="text-[10px] text-muted-foreground font-medium">
          Durchschnittlicher Schaden
        </p>
      </div>

      {/* Stats Grid: Downtime & Affected */}
      <div className="grid grid-cols-2 gap-1.5 mb-2">
        <div className="bg-muted/30 rounded-lg p-2 border border-border/30">
          <p className="text-[10px] text-muted-foreground mb-0.5">Ausfallzeit</p>
          <p className="text-xs font-bold text-destructive tabular-nums">
            {data.avgDowntime}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-2 border border-border/30">
          <p className="text-[10px] text-muted-foreground mb-0.5">Betroffene</p>
          <p className="text-xs font-bold text-foreground">
            {data.affectedPractices}
          </p>
        </div>
      </div>

      {/* City Stats Bar */}
      <div className="bg-accent/10 rounded-lg p-1.5 mb-2 border border-accent/20">
        <p className="text-[10px] font-semibold text-accent">
          📊 {data.cityStats}
        </p>
      </div>

      {/* Footer: Fun Fact */}
      <p className="text-[10px] text-muted-foreground leading-snug">
        💡 {data.funFact}
      </p>
      </div>
    </motion.div>
  );
};

export default AnimatedTooltip;
